import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { capturePromiseException } from '~/lib/metrics';
import { generateSwrCacheKeyForLink, StatusData } from '../actions/labels/label-lib';
import useSWR from 'swr';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { useAuth } from 'react-oidc-context';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { P2GBox, P2GImage, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useMemo } from 'react';
import ConsignmentSummaryParcels from '../panels/consignments/ConsignmentSummaryParcels';
import type { OrderLineParcelPudoCode } from '../actions/labels/label-lib';
import { hasStoredParcels } from '~/apps/smartsend/lib/parcels/itemView';
import { TenantSettingKeys } from '~/lib/consts/tenant-setting-keys';
import { AddToApple } from './AddToApple';
import { AddToGoogle } from './AddToGoogle';

interface props {
  statusEndpoint: string;
  wrapper: (content: React.ReactNode) => React.ReactElement;
}

interface orderlineprops {
  item?: SmartSendItemApiModel;
  statusEndpoint: string;
  wrapper: (content: React.ReactNode) => React.ReactElement;
  displayMode?: 'list' | 'full';
}

const ParcelCodes = ({ parcel }: { parcel: OrderLineParcelPudoCode }) => (
  <P2GBox key={parcel.parcelId} textAlign="center">
    <P2GBox>
      <P2GImage
        src={`data:image/png;base64,${parcel.dropOff.base64}`}
        style={{ maxWidth: '300px', margin: '5px', border: '1px solid #ccc' }}
        alt={parcel.dropOff.code}
      />
    </P2GBox>
    <P2GBox>
      <P2GText>
        <strong>{parcel.dropOff.code?.toUpperCase()}</strong>
      </P2GText>
    </P2GBox>
    <P2GBox
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'nowrap',
        gap: 2,
        textAlign: 'center',
        py: 1
      }}
    >
      {parcel.dropOff.appleWalletUrl && (
        <P2GLink target="_blank" href={parcel.dropOff.appleWalletUrl}>
          <AddToApple />
        </P2GLink>
      )}
      {parcel.dropOff.googleWalletUrl && (
        <P2GLink target="_blank" href={parcel.dropOff.googleWalletUrl}>
          <AddToGoogle />
        </P2GLink>
      )}
    </P2GBox>
  </P2GBox>
);

const usePudoStatus = (statusEndpoint: string | undefined) => {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();

  const cfg = useMemo(() => {
    if (!statusEndpoint) {
      return null;
    }

    const linkParams = new URL(statusEndpoint).searchParams;
    const orderId = linkParams.get('orderId') || '';
    const orderLineId = linkParams.get('orderLineId') || '';
    const labelHash = linkParams.get('hash') || '';

    return {
      endpoint: statusEndpoint,
      body: {
        ...(orderLineId ? { orderLineId: orderLineId } : { orderId: orderId }),
        preferredFileFormat: 'Png',
        fileProvisionType: 'Base64'
      },
      headers: {
        'Content-Type': 'application/json',
        'x-hash': labelHash
      }
    };
  }, [statusEndpoint]);

  const { data, isLoading } = useSWR<StatusData>(
    cfg?.endpoint &&
      generateSwrCacheKeyForLink({ Href: cfg.endpoint, Meta: cfg.body, Type: 'ORDERLINE-LABELS-STATUS' }),
    () =>
      cfg?.endpoint &&
      authenticatedFetchWithTenantAnalytics(user, tenant, settings, session, cfg.endpoint, {
        method: 'POST',
        body: JSON.stringify(cfg.body),
        headers: cfg.headers
      })
        .then((res) => res.json())
        .catch(capturePromiseException),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      revalidateOnMount: false,
      refreshInterval: (d) => (d === undefined ? 1000 : d.result.orderLinePudoCodes.length > 0 ? 0 : 2500) // Refresh every second if we dont have a pre-signed URL
    }
  );

  if (!cfg?.endpoint || !data?.result?.orderLinePudoCodes?.length) {
    return {
      codes: [],
      loading: isLoading,
      orderLinePrefix: settings[TenantSettingKeys.OrderLinePrefix]
    };
  }

  return {
    codes: data.result.orderLinePudoCodes,
    loading: isLoading,
    orderLinePrefix: settings[TenantSettingKeys.OrderLinePrefix]
  };
};

export const ItemPudoCodes = ({ item, wrapper, statusEndpoint, displayMode }: orderlineprops) => {
  const status = usePudoStatus(statusEndpoint);

  if (!status || status.codes.length === 0 || status.loading) {
    return null;
  }

  const contents = status.codes.reduce(
    (acc, o) => {
      o.parcels.forEach((p) => {
        acc[p.parcelId] = <ParcelCodes key={p.parcelId} parcel={p} />;
      });
      return acc;
    },
    {} as Record<string, React.ReactNode>
  );

  return wrapper(
    <P2GBox>
      {displayMode === 'full' && item && hasStoredParcels(item) ? (
        <ConsignmentSummaryParcels
          hideTotals={true}
          consignment={item.Consignment}
          currencyCode={item.CurrencyCode}
          keyedParcelContent={contents}
        />
      ) : (
        <P2GBox display="flex" gap={2} flexWrap="wrap" justifyItems="center" justifyContent="center">
          {status?.codes?.map((o) => (
            <P2GBox key={o.orderLineId}>
              {o.parcels.map((p) => (
                <ParcelCodes key={p.parcelId} parcel={p} />
              ))}
            </P2GBox>
          ))}
        </P2GBox>
      )}
    </P2GBox>
  );
};

const OrderPudoCodes = ({ wrapper, statusEndpoint }: props) => {
  const status = usePudoStatus(statusEndpoint);

  if (!status || status.codes.length === 0 || status.loading) {
    return null;
  }

  return wrapper(
    <P2GBox display="flex" gap={2} flexWrap="wrap" justifyItems="center" justifyContent="center">
      {status.codes.map((o) => (
        <P2GBox key={o.orderLineId}>
          {status.codes.length > 1 && (
            <P2GBox textAlign="center">
              <P2GText>
                <strong>
                  {status.orderLinePrefix}
                  {o.orderLineId}
                </strong>
              </P2GText>
            </P2GBox>
          )}
          <P2GBox>
            {o.parcels.map((p) => (
              <ParcelCodes key={p.parcelId} parcel={p} />
            ))}
          </P2GBox>
        </P2GBox>
      ))}
    </P2GBox>
  );
};

export default OrderPudoCodes;
